import fetchMock, { type FetchMockSandbox } from 'fetch-mock';

import type {
  EventNotificationRequest,
  ResourceNotificationRequest,
} from '@dynatrace-sdk/client-notification-v2';

class NotificationMock<T> {
  protected client: FetchMockSandbox = fetchMock.sandbox();

  protected listMock: fetchMock.MockOptions;
  protected createMock: fetchMock.MockOptions;
  protected destroyMock: fetchMock.MockOptions;

  constructor(
    public config: T,
    basePath: string,
  ) {
    fetchMock.config.overwriteRoutes = true;

    this.listMock = {
      url: `path:${basePath}`,
      method: 'get',
    };

    this.createMock = {
      url: `path:${basePath}`,
      method: 'post',
    };

    this.destroyMock = {
      url: `express:${basePath}/:id`,
      method: 'delete',
    };
  }

  reset(): void {
    fetchMock.reset();
  }

  list(
    scenario:
      | 'empty-notifications'
      | 'has-notification'
      | 'not-found'
      | 'no-read-permission',
  ) {
    const scenarios = {
      'empty-notifications': {
        status: 200,
        body: JSON.stringify({ count: 0, results: [] }),
      },
      'has-notification': {
        status: 200,
        body: JSON.stringify({
          count: 1,
          results: [{ id: '1234', ...this.config }],
        }),
      },
      'not-found': {
        status: 404,
      },
      'no-read-permission': {
        status: 403,
        body: JSON.stringify({
          error: {
            code: 403,
            message: 'Forbidden',
          },
        }),
      },
    } as const;

    fetchMock.mock(this.listMock, scenarios[scenario]);
  }

  create(
    scenario:
      | 'working'
      | 'no-write-permission'
      | 'limit-reached'
      | 'server-error',
  ) {
    const scenarios = {
      working: {
        status: 201,
        body: JSON.stringify({ id: '1234', ...this.config }),
      },
      'no-write-permission': {
        status: 403,
        body: JSON.stringify({
          error: {
            code: 403,
            message: 'Forbidden',
          },
        }),
      },
      'limit-reached': {
        status: 400,

        body: JSON.stringify({
          error: {
            details: {
              errorCode: 'eventNotificationLimitReached',
            },
          },
        }),
      },
      'server-error': 500,
    } as const;
    fetchMock.mock(this.createMock, scenarios[scenario]);
  }

  destroy(scenario: 'working' | 'no-write-permission') {
    const scenarios = {
      working: 204,
      'no-write-permission': {
        status: 403,
        body: JSON.stringify({
          error: {
            code: 403,
            message: 'Forbidden',
          },
        }),
      },
    } as const;

    fetchMock.mock(this.destroyMock, scenarios[scenario]);
  }

  hasCalled(method: 'list' | 'create' | 'destroy'): boolean {
    let methodMock: fetchMock.MockOptions | null = null;

    if (method === 'list') {
      methodMock = this.listMock;
    } else if (method === 'create') {
      methodMock = this.createMock;
    } else if (method === 'destroy') {
      methodMock = this.destroyMock;
    }

    if (methodMock === null) {
      return false;
    }

    return fetchMock.called(methodMock.url, methodMock.method);
  }
}

export class ResourceNotificationMock extends NotificationMock<ResourceNotificationRequest> {
  constructor(config?: ResourceNotificationRequest) {
    super(
      config ?? { notificationType: 'test', resourceId: 'test-id' },
      '/platform/notification/v2/resource-notifications',
    );
  }
}

export class EventNotificationMock extends NotificationMock<EventNotificationRequest> {
  constructor(config?: EventNotificationRequest) {
    super(
      config ?? {
        notificationType: 'event-query',
        resourceId: '996d8d4b-a7c8-4471-bbc0-88ffdd31b030',
        triggerConfiguration: {
          type: 'event',
          value: {
            query: 'event.type == "CUSTOM_ALERT"',
          },
        },
      },
      '/platform/notification/v2/event-notifications',
    );
  }
}
